import {
  HttpClient,
  HttpEvent,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BackendResponse,
  DocsTokenRequest,
  DocumentReference,
  ExtractionRequestResult,
  MessageSendRequest,
  SKIP_ERROR_HANDLING_HEADER,
  TokenApi,
} from '@qtek/shared/models';
import { Observable } from 'rxjs';

/**
 * `skipErrorHandling` opts a call out of the global HTTP error interceptor —
 * for bulk uploads that record failures per file, where the interceptor's
 * redirect to `/error` (500) or one alert per failed file would abort or
 * bury the rest of the batch.
 */
export interface DocumentServingRequestOptions {
  skipErrorHandling?: boolean;
}

const requestHeaders = (
  options: DocumentServingRequestOptions
): HttpHeaders | undefined =>
  options.skipErrorHandling
    ? new HttpHeaders().append(SKIP_ERROR_HANDLING_HEADER, '')
    : undefined;

// The Angular service worker swallows XHR upload-progress events for requests it proxies.
// A query param, not the header, so a cross-origin API host needs no CORS preflight change.
const NGSW_BYPASS_PARAM = 'ngsw-bypass';

/**
 * `POST /api/v2/documents/token` + friends — everything under the "Document
 * serving APIs" swagger tag that isn't already covered by
 * `buildDocumentServingUrl()` (preview/thumbnail/download, GET-only) or
 * `DocumentReferenceService`/its store (`/api/v2/model/<Entity>DocumentReference`
 * CRUD). This is the two-stage upload token flow plus send/notify/zip.
 */
@Injectable({ providedIn: 'root' })
export class DocumentServingService {
  private http = inject(HttpClient);

  /**
   * Mints an upload/download JWT and — for `action: UPLOAD` — provisions the
   * document/reference row(s) up front.
   */
  generateToken(
    payload: DocsTokenRequest,
    options: DocumentServingRequestOptions = {}
  ): Observable<BackendResponse<TokenApi>> {
    return this.http.post<BackendResponse<TokenApi>>(
      '/api/v2/documents/token',
      payload,
      { withCredentials: true, headers: requestHeaders(options) }
    );
  }

  /**
   * `POST /api/v2/documents/upload-tokenless` — single-step upload: attaches
   * a file to an existing entity (or a new one created on demand via
   * `entityJson`, see `buildDocumentReferenceFormData`) in one request.
   * `formData` must match the upload contract — build it with
   * `buildDocumentReferenceFormData()`, don't hand-build it. Response shape
   * is ambiguous (`{res: T}` or bare `T`) — callers unwrap both, see
   * `document-reference.store.ts`'s `upload()`.
   */
  uploadTokenless(
    formData: FormData,
    options: DocumentServingRequestOptions = {}
  ): Observable<
    HttpEvent<BackendResponse<DocumentReference> | DocumentReference>
  > {
    return this.http.post<
      BackendResponse<DocumentReference> | DocumentReference
    >('/api/v2/documents/upload-tokenless', formData, {
      withCredentials: true,
      reportProgress: true,
      observe: 'events',
      headers: requestHeaders(options),
      params: new HttpParams()
        .set('auditNames', 'true')
        .set(NGSW_BYPASS_PARAM, 'true'),
    });
  }

  /**
   * `POST /api/v2/documents/upload-token?token=&_id=` — uploads bytes
   * against a document pre-provisioned by `generateToken`. `documentId` is
   * only required when the token authorises more than one document.
   * Returns the finished document reference row directly.
   */
  uploadWithToken(
    file: File,
    token: string,
    documentId?: string,
    fileName?: string,
    options: DocumentServingRequestOptions = {}
  ): Observable<HttpEvent<BackendResponse<DocumentReference>>> {
    const formData = new FormData();
    formData.append('file', file, fileName ?? file.name);

    let params = new HttpParams()
      .set('token', token)
      .set('auditNames', 'true')
      .set(NGSW_BYPASS_PARAM, 'true');
    if (documentId) {
      params = params.set('_id', documentId);
    }

    return this.http.post<BackendResponse<DocumentReference>>(
      '/api/v2/documents/upload-token',
      formData,
      {
        withCredentials: true,
        reportProgress: true,
        observe: 'events',
        params,
        headers: requestHeaders(options),
      }
    );
  }

  /**
   * `POST /api/v2/documents/extract` — runs the term-sheet / credit-agreement /
   * covenant extractors on a document that already finished its upload pipeline
   * (SD-2668).
   *
   * Data-room uploads deliberately skip extraction, so this is what the opt-in
   * modal calls when the user accepts. Asynchronous: it returns as soon as the
   * request is queued, and results arrive as `docmeta` over the websocket —
   * extraction itself can take a couple of minutes.
   *
   * Takes the *typed* reference id (`ITEM=<uuid>`), not a bare document id, and
   * requires edit access to the document.
   */
  requestExtraction(
    referenceId: string,
    force = false
  ): Observable<BackendResponse<ExtractionRequestResult>> {
    let params = new HttpParams().set('referenceId', referenceId);
    if (force) {
      params = params.set('force', 'true');
    }
    return this.http.post<BackendResponse<ExtractionRequestResult>>(
      '/api/v2/documents/extract',
      null,
      { params, withCredentials: true }
    );
  }

  /** `POST /api/v2/documents/{documentId}/send` — share a document by email. */
  sendDocument(
    documentId: string,
    payload: MessageSendRequest
  ): Observable<BackendResponse<string[]>> {
    return this.http.post<BackendResponse<string[]>>(
      `/api/v2/documents/${documentId}/send`,
      payload,
      { withCredentials: true }
    );
  }

  /** `POST /api/v2/deals/{itemId}/notify-documents` — "new documents" email to the deal team. */
  notifyDocuments(
    itemId: string,
    payload: MessageSendRequest
  ): Observable<BackendResponse<string[]>> {
    return this.http.post<BackendResponse<string[]>>(
      `/api/v2/deals/${itemId}/notify-documents`,
      payload,
      { withCredentials: true }
    );
  }

  /**
   * Builds a `GET /api/v2/documents/zip` URL (bulk download, foldered by
   * category) for `window.location.href` navigation — same idiom as
   * `buildDocumentServingUrl`, not an `HttpClient` call, since this streams
   * a file rather than returning JSON.
   */
  zip(
    itemId: string,
    options: { baseUrl?: string; referenceIds?: readonly string[] } = {}
  ): string {
    const search = new URLSearchParams({ itemId });
    if (options.referenceIds?.length) {
      search.set('referenceIds', options.referenceIds.join(','));
    }
    return `${options.baseUrl ?? ''}/api/v2/documents/zip?${search.toString()}`;
  }

  /**
   * Builds a `GET /api/v2/documents/manifest/csv` URL — every document of
   * the deal the caller may view, with its folder path, size and upload
   * outcome (incl. uploads that never finished). Navigated to, like `zip`.
   */
  manifestCsv(itemId: string, options: { baseUrl?: string } = {}): string {
    const search = new URLSearchParams({ itemId });
    return `${options.baseUrl ?? ''}/api/v2/documents/manifest/csv?${search.toString()}`;
  }
}
